import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { TelHotelSearchRedirectParams } from './routes/HotelSearch/HotelSearch.types';
import type { GuestPartnerHotelBookingRedirectParams, TelemetryHotelInfo } from './HotelBooking.types';
import type { PartialUtmParameters } from '@shared/utils/utmParameters';

export type PagesForTelemetry = 'hotelbooking' | 'roomblockhotel' | 'hotelbookingPartner' | 'accommodationsPlace' | 'hotelbookingPartnerSearch' | 'hotelDetails' | 'hotelSearch';

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  // If event have hotel booking link and redirect to booking link
  hotelbooking: (userRole: string) => ({
    category: 'travel',
    pagePrefix: 'guest',
    page: 'hotelbooking',
    extraPageInfo: {
      role: userRole,
    },
  }),
  // Reserved room block redirect
  roomblockhotel: (userRole: string) => ({
    category: 'travel',
    pagePrefix: 'guest',
    page: 'roomblockhotel',
    extraPageInfo: {
      role: userRole,
    },
  }),
  // Existing accommodations place redirect
  accommodationsPlace: (userRole: string) => ({
    category: 'travel',
    pagePrefix: 'guest',
    page: 'accommodationsPlace',
    extraPageInfo: {
      role: userRole,
    },
  }),
  // Existing hotel pdp redirect
  hotelbookingPartner: (userRole: string) => ({
    category: 'travel',
    pagePrefix: 'guest',
    page: 'hotelbookingPartner',
    extraPageInfo: {
      role: userRole,
    },
  }),
  // Existing hotel search redirect
  hotelbookingPartnerSearch: (userRole: string) => ({
    category: 'travel',
    pagePrefix: 'guest',
    page: 'hotelbookingPartnerSearch',
    extraPageInfo: {
      role: userRole,
    },
  }),

  // New hotel details redirect
  hotelDetails: () => ({ category: 'travel', pagePrefix: 'guest', page: 'hotelDetails' }),
  // New hotel search redirect
  hotelSearch: () => ({ category: 'travel', pagePrefix: 'guest', page: 'hotelSearch' }),
};

const hotelBookingGuestSiteTelemetry = createTelemetryObject({
  actions: {
    accommodationPlaceSoldOutDialog: (args: TelemetryHotelInfo & { dialogAction: 'See nearby hotels' | 'Back to Hosts Hotel List' }) => ({
      action: 'TravelButtonInteracted',
      category: 'travel',
      extraInfo: {
        name: 'accommodationPlaceSoldOutDialog',
        ...args,
      },
    }),
    partnerHotelRedirectInterceptorDialog: (args: TelemetryHotelInfo & { dialogAction: 'accepted' | 'declined' }) => ({
      action: 'TravelButtonInteracted',
      category: 'travel',
      extraInfo: {
        name: 'partnerHotelRedirectInterceptorDialog',
        ...args,
      },
    }),
    guestPartnerHotelBookingRedirect: ({ telemetryName, destinationUrl, onTelemetrySend, ...args }: GuestPartnerHotelBookingRedirectParams) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'hotelbooking',
      extraInfo: {
        name: telemetryName ?? 'guestPartnerHotelBookingRedirect',
        destinationURL: destinationUrl?.toString(),
        destinationDomain: destinationUrl?.hostname,
        ...args,
      },
      callback: onTelemetrySend,
    }),
    partnerHotelSearchRedirect: ({ destinationName, destinationUrl, onTelemetrySend, ...args }: TelHotelSearchRedirectParams) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'hotelbooking',
      extraInfo: {
        name: 'guestPartnerHotelSearchRedirect',
        destinationName,
        destinationURL: destinationUrl?.toString(),
        destinationDomain: destinationUrl?.hostname,
        ...args,
      },
      callback: onTelemetrySend,
    }),
    guestHotelBookingRedirect: (destinationURL: string, destinationDomain: string, success: boolean, utmParams?: PartialUtmParameters) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'hotelbooking',
      extraInfo: {
        name: 'guestHotelBookingRedirect',
        destinationURL: destinationURL || '',
        destinationDomain: destinationDomain || '',
        success,
        ...utmParams,
      },
    }),
    guestHotelBookingQueryError: (utmParams?: PartialUtmParameters) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'hotelbooking',
      extraInfo: {
        name: 'guestHotelBookingRedirectQueryError',
        ...utmParams,
      },
    }),
    guestRoomblockHotelRedirect: (destinationURL: string, destinationDomain: string, success: boolean, callback?: () => void, utmParams?: PartialUtmParameters) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'roomblockhotel',
      extraInfo: {
        name: 'guestRoomblockHotelRedirect',
        destinationURL: destinationURL || '',
        destinationDomain: destinationDomain || '',
        success,
        ...utmParams,
      },
      callback,
    }),
    guestRoomblockHotelQueryError: (callback: () => void, utmParams?: PartialUtmParameters) => ({
      action: 'GuestHotelBookingRedirect',
      category: 'travel',
      label: 'roomblockhotel',
      extraInfo: {
        name: 'guestRoomblockHotelQueryError',
        ...utmParams,
      },
      callback,
    }),
  },
  pages,
});

const { TelemetryProvider, useTelemetry: useHotelBookingGuestSiteTelemetry } = createTelemetry(hotelBookingGuestSiteTelemetry);
export { TelemetryProvider, useHotelBookingGuestSiteTelemetry };
